import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { ADMIN_COLORS } from '@/constants/admin';
import { useResidentTransactions } from '@/hooks/admin/useResidentTransactions';
import { getApiErrorDescription } from '@/utils/admin/errorHandling';
import { TransactionList } from '@/components/admin/resident/mobile/TransactionList';
import { TransactionSummary } from '@/components/admin/resident/common/TransactionSummary';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AdminErrorBoundary } from '@/components/admin/AdminErrorBoundary';
import { BaseAdminScreen } from '@/components';

type TransactionHistoryScreenProps = StackScreenProps<
  AdminStackParamList,
  'TransactionHistory'
>;

export const TransactionHistoryScreen: React.FC<TransactionHistoryScreenProps> = ({
  navigation,
  route,
}) => {
  const { accountId, leaseId, residentName } = route.params;
  const [refreshing, setRefreshing] = useState(false);

  const { transactions, summary, isLoading, error, refetch } = useResidentTransactions({
    accountId,
    leaseId,
    autoFetch: true,
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const handleClose = () => {
    navigation.goBack();
  };

  const renderContent = () => {
    if (isLoading && transactions.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <LoadingSpinner message="Loading transactions..." />
        </View>
      );
    }

    if (error && transactions.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Text style={styles.errorTitle}>Unable to Load Transactions</Text>
          <Text style={styles.errorMessage}>
            {getApiErrorDescription(error, 'Something went wrong while loading the transaction history.')}
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={refetch}>
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      );
    }

    if (transactions.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Text style={styles.emptyTitle}>No Transactions</Text>
          <Text style={styles.emptyMessage}>
            No transactions found for this resident.
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.contentContainer}>
        {/* Summary */}
        <View style={styles.summaryContainer}>
          <TransactionSummary summary={summary} />
        </View>

        {/* Transaction List */}
        <View style={styles.listContainer}>
          <TransactionList 
            transactions={transactions}
            maxRows={undefined} // Show all
          />
        </View>
      </View>
    );
  };

  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="TransactionHistory"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity onPress={handleClose} style={styles.backButton}>
              <Text style={styles.backButtonText}>← Back</Text>
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle}>Transaction History</Text>
              {residentName && (
                <Text style={styles.headerSubtitle}>{residentName}</Text>
              )}
            </View>
          </View>

          {/* Content */}
          {renderContent()}
        </View>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  header: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  backButton: {
    marginBottom: 12,
  },
  backButtonText: {
    fontSize: 16,
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '600',
  },
  headerTitleContainer: {
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  contentContainer: {
    flex: 1,
  },
  summaryContainer: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  listContainer: {
    flex: 1,
    padding: 16,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 6,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
});

